"use client"

import { usePathname, useShallowRouting } from "@opensea/navigation"
import { useIsHydrated, useOnChange } from "@opensea/ui-kit/hooks"
import type { Nullable } from "@opensea/utils"
import {
  createContext,
  type Dispatch,
  type SetStateAction,
  useContext,
  useState,
} from "react"
import { Onboarding } from "./Onboarding"
import { OnboardingFadeIn } from "./OnboardingFadeIn"

type Route = Nullable<string>

type OnboardingGuardContextType = {
  visible: boolean

  redirect?: Route
  setRedirect: Dispatch<SetStateAction<Route>>

  force?: boolean
  setForce: Dispatch<SetStateAction<boolean>>

  completed: boolean
  setCompleted: Dispatch<SetStateAction<boolean>>
}

const OnboardingGuardContext = createContext<OnboardingGuardContextType>({
  visible: false,

  redirect: null,
  setRedirect: () => null,

  force: false,
  setForce: () => null,

  completed: false,
  setCompleted: () => null,
})

type Props = {
  children: React.ReactNode
}

export function OnboardingGuard({ children }: Props) {
  const pathname = usePathname()
  const { replace: shallowReplace } = useShallowRouting()

  const [force, setForce] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [redirect, setRedirect] = useState<Route>(null)

  const isHydrated = useIsHydrated()
  const isClient = typeof window !== "undefined" && isHydrated

  const fadeComponent = redirect && <OnboardingFadeIn />

  function renderOnboardingComponent() {
    if (!isClient) {
      return
    }

    if (completed) {
      return
    }

    // If explicitly forced (e.g., from the nudge), render the full onboarding flow.
    if (force) {
      return <Onboarding />
    }

    // Onboarding nudge has been removed as per team decision
    // Users can still access onboarding via /onboard URL if needed
    return
  }
  const onboardingComponent = renderOnboardingComponent()

  /**
   * Redirect if onboarding is not going to be shown
   */
  useOnChange([isClient, onboardingComponent, redirect, pathname], () => {
    if (isClient && !onboardingComponent && redirect && redirect !== pathname) {
      shallowReplace(redirect)
      setRedirect(null)
    }
  })

  const contextValue = {
    visible: Boolean(onboardingComponent),
    redirect,
    setRedirect,
    force,
    setForce,
    completed,
    setCompleted,
  }

  return (
    <OnboardingGuardContext.Provider value={contextValue}>
      {children}
      {fadeComponent}
      {onboardingComponent}
    </OnboardingGuardContext.Provider>
  )
}

export function useOnboardingGuard() {
  return useContext(OnboardingGuardContext)
}

export function useOnboarding(
  options: Pick<OnboardingGuardContextType, "force" | "redirect">,
) {
  const isHydrated = useIsHydrated()
  const { setForce, setRedirect } = useOnboardingGuard()

  useOnChange(isHydrated, () => {
    setForce(options.force ?? false)
    setRedirect(options.redirect)
  })
}
